import { ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Presentational pieces shared by the two multi-select widgets — the sync
// fixed-option `MultiSelect` and the async/searchable `EntityMultiSelect`.
// Keeps the trigger summary, the popover trigger button, and the Clear footer
// from drifting between the two. No edits to components/ui (G9).

// Moved to `@clockwise/ui` so the shared `Picker` summarises its trigger the
// same way these do. Re-exported because this is the path the app's
// multi-selects already import.
export { summarizeSelection } from '@clockwise/ui';

/** The outline trigger button (summary + chevron). forwardRef so Radix's
 *  `PopoverTrigger asChild` can attach its ref/handlers. */
export const MultiSelectTrigger = React.forwardRef<
  HTMLButtonElement,
  { summary: string; className?: string } & React.ComponentPropsWithoutRef<typeof Button>
>(({ summary, className, ...rest }, ref) => (
  <Button
    ref={ref}
    type="button"
    variant="outline"
    role="combobox"
    // This is a Select/Combobox-equivalent filter trigger, not an action
    // button — `data-tone="form"` (globals.css) swaps Outline's bold lime
    // hover fill for the same border-only hover the neighbouring
    // `combobox-trigger`/`select-trigger` filters use.
    data-tone="form"
    // `rounded-full` stated directly: `PopoverTrigger asChild` lets Radix's
    // Slot clobber this Button's own `data-slot="button"` with the
    // trigger's `data-slot="popover-trigger"`, so the global
    // `[data-slot='button']` pill-radius rule never matches it.
    className={cn('justify-between rounded-full font-normal', className)}
    {...rest}
  >
    <span className="truncate">{summary}</span>
    <ChevronsUpDown className="ml-2 shrink-0 opacity-50" />
  </Button>
));
MultiSelectTrigger.displayName = 'MultiSelectTrigger';

/** The Clear footer row, shown only when something is selected. */
export function MultiSelectClearButton({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <div className="border-t pt-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full justify-start text-muted-foreground"
        onClick={onClear}
      >
        {label}
      </Button>
    </div>
  );
}
